import { useRef } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { landingColors, landingFont, eyebrowSx, sectionHeadingSx } from '../landingTheme';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { getLandingIcon } from '../landingIcons';
import { HOW_IT_WORKS_STEPS } from '../landingContent';

export default function HowItWorksSection() {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);

  return (
    <Box
      ref={sectionRef}
      component="section"
      id="how-it-works-section"
      aria-label="How It Works"
      sx={{ py: { xs: 8, md: 14 }, backgroundColor: landingColors.paper }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2.5, md: 4 } }}>
        <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: { xs: 5, md: 8 } }}>
          <Typography data-reveal sx={{ ...eyebrowSx, color: landingColors.indigo, fontFamily: landingFont, mb: 1.5 }}>
            How It Works
          </Typography>
          <Typography data-reveal data-reveal-delay="0.06" component="h2" sx={{ ...sectionHeadingSx, fontFamily: landingFont, color: landingColors.ink, mb: 2 }}>
            From scan to served in four steps
          </Typography>
          <Typography data-reveal data-reveal-delay="0.12" sx={{ fontFamily: landingFont, color: landingColors.inkMuted, fontSize: { xs: '1rem', md: '1.2rem' }, lineHeight: 1.6 }}>
            A seamless flow your guests already know how to use. No downloads, no friction.
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: { xs: 2.5, md: 3 } }}>
          {HOW_IT_WORKS_STEPS.map((step, index) => {
            const Icon = getLandingIcon(step.icon);
            return (
              <Box
                key={step.step}
                data-reveal
                data-reveal-delay={(index * 0.08).toString()}
                sx={{
                  position: 'relative',
                  p: { xs: 3, md: 3.5 },
                  borderRadius: '20px',
                  backgroundColor: landingColors.paperAlt,
                  border: `1px solid ${landingColors.line}`,
                  height: '100%',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 18px 40px rgba(20,20,31,0.08)' },
                }}
              >
                <Typography sx={{ fontFamily: landingFont, fontWeight: 800, fontSize: '0.95rem', color: landingColors.indigo, letterSpacing: '0.1em', mb: 2 }}>
                  {step.step}
                </Typography>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '14px',
                    backgroundColor: landingColors.indigo,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                  }}
                >
                  <Icon sx={{ fontSize: 28 }} />
                </Box>
                <Typography component="h3" sx={{ fontFamily: landingFont, fontWeight: 700, fontSize: '1.15rem', color: landingColors.ink, mb: 1 }}>
                  {step.title}
                </Typography>
                <Typography sx={{ fontFamily: landingFont, color: landingColors.inkMuted, fontSize: '0.92rem', lineHeight: 1.6 }}>
                  {step.description}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
}
